import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, Plus, PencilSimple, Trash, Eye, CheckCircle, ArrowsClockwise, ClipboardText, Shrimp, Package, CurrencyDollar, UserCircle } from "@phosphor-icons/react";
import Pagination from "@/components/Pagination";
import ExportButtons from "@/components/ExportButtons";
import { exportToExcel, exportToPDF } from "@/lib/export";

const ACTION_ICONS = {
  create: { icon: Plus, color: "text-emerald-600", bg: "bg-emerald-50", label: "Buat" },
  update: { icon: PencilSimple, color: "text-blue-600", bg: "bg-blue-50", label: "Edit" },
  delete: { icon: Trash, color: "text-red-600", bg: "bg-red-50", label: "Hapus" },
  verify: { icon: CheckCircle, color: "text-purple-600", bg: "bg-purple-50", label: "Verifikasi" },
};

const COL_LABELS = {
  tambak: "Tambak", siklus: "Siklus", laporan: "Laporan", gudang: "Gudang", panen: "Panen",
  keuangan: "Keuangan", users: "Pengguna", monitoring: "Monitoring", profil_koperasi: "Profil",
};

const EXPORT_COLS = [
  { header: "Waktu", accessor: (r) => r.timestamp ? new Date(r.timestamp).toLocaleString("id-ID") : "" },
  { header: "Pengguna", accessor: (r) => r.user_name },
  { header: "Role", accessor: (r) => r.user_role },
  { header: "Aksi", accessor: (r) => r.action },
  { header: "Modul", accessor: (r) => r.collection },
  { header: "Detail", accessor: (r) => r.detail || "-" },
];

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Baru saja";
  if (m < 60) return `${m}m lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}j lalu`;
  const d = Math.floor(h / 24);
  return `${d}h lalu`;
}

export default function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/audit-log?page=${page}&limit=20&search=${search}`);
      setItems(data.data);
      setTotal(data.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="audit-log-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Audit Trail</h1>
          <p className="text-sm text-slate-500 mt-1">Riwayat aktivitas dan perubahan data</p>
        </div>
        <ExportButtons onExcel={() => exportToExcel(items, EXPORT_COLS, "audit_log")} onPDF={() => exportToPDF(items, EXPORT_COLS, "audit_log", "Audit Trail P3UW")} />
      </div>

      <div className="relative w-full sm:w-80">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="search-audit" placeholder="Cari aktivitas..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" />
      </div>

      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {loading ? (
              <div className="text-center py-12 text-slate-400 text-sm">Memuat...</div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">Belum ada aktivitas tercatat</div>
            ) : items.map((item) => {
              const acfg = ACTION_ICONS[item.action] || ACTION_ICONS.update;
              const IconComp = acfg.icon;
              return (
                <div key={item._id} data-testid={`audit-item-${item._id}`} className="flex items-start gap-3 px-4 sm:px-6 py-3 hover:bg-slate-50 transition-colors">
                  <div className={`w-8 h-8 rounded-lg ${acfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <IconComp size={15} weight="bold" className={acfg.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#1A1A1A]">{item.user_name}</span>
                      <Badge variant="secondary" className="text-[10px] h-5">{item.user_role}</Badge>
                      <Badge className={`${acfg.bg} ${acfg.color} hover:${acfg.bg} text-[10px] h-5`}>{acfg.label}</Badge>
                      <Badge variant="outline" className="text-[10px] h-5">{COL_LABELS[item.collection] || item.collection}</Badge>
                    </div>
                    {item.detail && <p className="text-xs text-slate-500 mt-0.5 truncate">{item.detail}</p>}
                  </div>
                  <span className="text-[10px] text-slate-400 shrink-0 mt-1">{timeAgo(item.timestamp)}</span>
                </div>
              );
            })}
          </div>
          <div className="px-4 pb-3 border-t border-slate-100">
            <Pagination page={page} limit={20} total={total} onPageChange={setPage} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
